import {Select} from '@mantine/core';
import {ColorSwatch} from 'src/design/atoms/ColorSwatch';
import {Group} from 'src/design/atoms/Group';
import {Text} from 'src/design/atoms/Text';
import {OptionButtonGroup} from 'src/design/atoms/OptionButtonGroup';
import {renderFieldLabel} from 'src/app/forms/renderFieldLabel';
import {
  optionColorSwatches,
  toOptionButtonGroupColor,
} from 'src/app/forms/optionColors';
import React, {useCallback, useMemo} from 'react';
import type {IFormFieldV2} from 'src/types/FormV2';

const BUTTON_GROUP_MAX_OPTIONS = 5;
const BUTTON_GROUP_MAX_LABEL_LENGTH = 24;

export interface FormSelectV2Props {
  field: IFormFieldV2;
  value: string | number;
  error?: string;
  onChange: (
    name: string,
    value: string | number,
    displayValue?: string
  ) => void;
}

export const FormSelectV2 = React.memo<FormSelectV2Props>(
  ({field, value, error, onChange}: FormSelectV2Props) => {
    const options = useMemo(
      () => field.options ?? field.builderOptions?.selectOptions ?? [],
      [field.options, field.builderOptions?.selectOptions]
    );

    const data = useMemo(
      () =>
        options.map(opt => ({
          value: String(opt.id),
          label: opt.label,
          color: opt.color,
        })),
      [options]
    );

    const handleChange = useCallback(
      (val: string | null) => {
        const selectedOption = field.options?.find(
          opt => String(opt.id) === val
        );
        onChange(field.name, val || '', selectedOption?.label);
      },
      [field.name, field.options, onChange]
    );

    const fitsButtonGroup =
      options.length > 0 &&
      options.length <= BUTTON_GROUP_MAX_OPTIONS &&
      options.every(opt => opt.label.length <= BUTTON_GROUP_MAX_LABEL_LENGTH);

    if (fitsButtonGroup) {
      return (
        <OptionButtonGroup
          label={renderFieldLabel(field)}
          ariaLabel={field.label}
          description={field.description}
          required={field.required}
          error={error}
          options={data.map(opt => ({
            value: opt.value,
            label: opt.label,
            color: toOptionButtonGroupColor(opt.color),
          }))}
          value={value ? String(value) : null}
          onChange={handleChange}
          allowDeselect={!field.required}
          disabled={field.disabled}
        />
      );
    }

    return (
      <Select
        data-testid={`form-field-${field.name}`}
        label={renderFieldLabel(field)}
        description={field.description}
        error={error}
        value={value ? String(value) : null}
        onChange={handleChange}
        data={data}
        renderOption={({option}) => {
          const color = toOptionButtonGroupColor(
            data.find(opt => opt.value === option.value)?.color
          );
          return (
            <Group gap="xs" wrap="nowrap">
              {color && (
                <ColorSwatch color={optionColorSwatches[color]} size={12} />
              )}
              <Text size="sm">{option.label}</Text>
            </Group>
          );
        }}
        withAsterisk={field.required}
        placeholder={`Select ${field.label.toLowerCase()}`}
        searchable
        clearable={!field.required}
        // Required selects must never be cleared. Mantine's Select defaults
        // allowDeselect to true, so re-selecting the active option fires
        // onChange(null) and empties a required value. Disable that here.
        allowDeselect={!field.required}
        disabled={field.disabled}
        // We set the zIndex here due to the FormPreview component in Form Builder.
        // Eventually, we'll remove this and use the default zIndex
        comboboxProps={{zIndex: 1001}}
      />
    );
  }
);
